import classNames from 'classnames'
import { default as ReactAsyncSelect } from 'react-select/async'

import { Option, Options } from './types'
import { getOnChangeFunctionSafely } from './utils'
import { FieldLabel } from '@/Atomic/Atoms/FieldLabel'
import { Icon } from '@/Atomic/Atoms/Icon'

import '../FieldStates.scss'

interface AsyncSelectProps {
  name: string
  label?: string
  requirementState?: 'required' | 'optional'
  displayValue?: string
  value?: unknown
  defaultValue?: unknown
  onChange: (...event: unknown[]) => void
  loadReferenceOptions?: (inputValue: string) => Promise<Options>
  placeholder?: string
  className?: string
  isDisabled?: boolean
  autoFocus?: boolean
  openMenuOnFocus?: boolean
  error?: string
}

export const AsyncSelect: React.FC<AsyncSelectProps> = ({
  name,
  label,
  requirementState,
  displayValue,
  value,
  defaultValue,
  onChange,
  loadReferenceOptions,
  placeholder,
  className,
  isDisabled,
  autoFocus,
  openMenuOnFocus,
  error,
}) => {
  const isControlled = value !== undefined
  const effectiveValue = isControlled ? value : defaultValue
  const hasValue = effectiveValue != null && effectiveValue !== ''
  // Without a value react-select has to receive null, otherwise an option with
  // an empty label is rendered as the selection and hides the placeholder.
  const def: Option | null = hasValue
    ? {
        label: displayValue ?? String(effectiveValue),
        value: String(effectiveValue),
      }
    : null
  const onChangeFunction = (val: unknown) =>
    getOnChangeFunctionSafely(onChange, val)

  return (
    <div
      className={classNames(className, {
        'field--readonly': isDisabled,
        'field-invalid': error,
      })}
    >
      <FieldLabel
        name={name}
        label={label}
        requirementState={requirementState}
      />
      <ReactAsyncSelect
        name={name}
        defaultOptions={true}
        {...(isControlled ? { value: def } : { defaultValue: def })}
        onChange={onChangeFunction}
        loadOptions={loadReferenceOptions}
        placeholder={placeholder}
        className="basic-select-container"
        classNamePrefix="basic-select"
        isDisabled={isDisabled}
        isClearable
        autoFocus={autoFocus}
        openMenuOnFocus={openMenuOnFocus}
      />
      {error && (
        <div className="field-error" role="alert">
          <Icon icon="info-circle" size="sm" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}
